import * as swcHelpers from '@swc/helpers';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { QueryString, optionalKey } from '@grandvisionhq/utils';
import { verify, optional, Type, either, constrain, Constraint, adjust } from 'specified';
import { v4 } from 'uuid';
import { useReducer, useEffect } from 'react';

class ResponseError extends Error {
    constructor(message, statusCode, customData){
        super(message);
        this.statusCode = statusCode;
        this.customData = customData;
        // Maintains proper stack trace for where our error was thrown
        if (typeof Error.captureStackTrace === 'function') {
            Error.captureStackTrace(this, this.constructor);
        } else {
            this.stack = new Error(message).stack;
        }
        this.statusCode = statusCode;
        this.customData = customData;
        // Make sure instanceof works properly
        // https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, ResponseError.prototype);
    }
}

const okOrThrow = (response)=>{
    if (!response.ok) {
        const error = new ResponseError(`Request to ${response.url} failed: ${response.status} - ${response.statusText}.`, response.status);
        throw error;
    }
};
function okOrThrowWithResponseError(response, responseMapper) {
    return _okOrThrowWithResponseError.apply(this, arguments);
}
function _okOrThrowWithResponseError() {
    _okOrThrowWithResponseError = swcHelpers.asyncToGenerator(function*(response, responseMapper) {
        if (response.ok) {
            return;
        }
        let isHandled = false;
        try {
            const result = yield response.json();
            const error = new ResponseError(result.message, response.status, responseMapper === null || responseMapper === void 0 ? void 0 : responseMapper(result));
            isHandled = true;
            throw error;
        } catch (error) {
            // check if the error is already handled
            if (isHandled) {
                throw error;
            }
            // otherwise fallback to default error handling
            okOrThrow(response);
        }
    });
    return _okOrThrowWithResponseError.apply(this, arguments);
}
const unwrapResponse = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(response, options = {}) {
        if (response.status === 422) {
            return response.json().then((error)=>{
                throw error;
            });
        }
        if (options.useResponseError || options.responseErrorMapper) {
            yield okOrThrowWithResponseError(response, options.responseErrorMapper);
        } else {
            yield okOrThrow(response);
        }
        return yield response.json();
    });
    return function unwrapResponse(response) {
        return _ref.apply(this, arguments);
    };
}();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateResponse = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(spec, response) {
        okOrThrow(response);
        const { err , value  } = verify(spec, (yield response.json()));
        if (err) {
            throw err;
        }
        return value();
    });
    return function validateResponse(spec, response) {
        return _ref.apply(this, arguments);
    };
}();

const jsonMimeType = 'application/json';
const acceptJson = {
    Accept: jsonMimeType
};
const postJson = {
    'Content-Type': jsonMimeType
};
const authHeader = (accessToken)=>({
        Authorization: `Bearer ${accessToken}`
    })
;
const correlationHeader = ()=>{
    return {
        'X-Correlation-ID': v4()
    };
};
const acceptLanguageHeader = (locale)=>locale ? {
        'Accept-Language': locale
    } : {}
;

const typename = (...names)=>optional(Type.literal(names.reduce((acc, name)=>swcHelpers.objectSpread({}, acc, {
            [name]: 1
        })
    , {})))
;
const durationSchema = {
    __typename: typename('Duration'),
    amount: Type.number,
    unit: Type.literal({
        MINUTES: 1,
        HOURS: 1
    })
};
const priceSchema = {
    __typename: typename('Price'),
    amount: Type.number,
    currency: Type.string
};
const appointmentTypeSchema = {
    __typename: typename('AppointmentType'),
    customerRequired: Type.boolean,
    duration: Type.interface(durationSchema),
    id: Type.string,
    label: Type.string,
    name: Type.string,
    additionalTags: optional(Type.map(Type.string, Type.string)),
    opticianRequired: Type.boolean,
    price: either(Type.null, Type.interface(priceSchema)),
    tryInStore: Type.boolean,
    visibleToCustomers: Type.boolean
};
const appointmentSchema = {
    __typename: typename('Appointment'),
    id: Type.string,
    status: Type.string,
    startTime: Type.string,
    endTime: Type.string,
    appointmentType: Type.interface({
        __typename: typename('AppointmentType'),
        id: Type.string,
        name: Type.string,
        duration: Type.interface(durationSchema)
    }),
    store: Type.interface({
        __typename: typename('Store', 'AppointmentStore'),
        id: Type.string,
        code: Type.string,
        name: Type.string
    })
};
const appointmentResponseSpec = Type.interface(appointmentSchema);
const appointmentListResponseSpec = Type.array(appointmentResponseSpec);
const appointmentTypeResponseSpec = Type.interface(appointmentTypeSchema);
const appointmentTypesResponseSpec = Type.array(Type.interface(appointmentTypeSchema));
const availabilityResponseSpec = Type.array(Type.interface({
    __typename: typename('Availability'),
    percentageAvailable: Type.number,
    slotsAvailable: Type.number,
    storeCode: Type.string,
    perDay: Type.map(Type.string, Type.interface({
        slotsAvailable: Type.number,
        percentageAvailable: Type.number
    }))
}));
const createAppointmentResponseSpec = Type.interface({
    __typename: typename('Appointment'),
    id: Type.string,
    startTime: Type.string,
    endTime: Type.string,
    status: Type.string,
    createdBy: either(Type.string, Type.null),
    customerComment: either(Type.string, Type.null),
    bookingChannel: either(Type.string, Type.null),
    store: Type.interface({
        __typename: typename('Store', 'AppointmentStore'),
        id: Type.string,
        code: Type.string,
        name: Type.string,
        phone: either(Type.string, Type.null),
        address: Type.interface({
            __typename: typename('StoreAddress', 'AppointmentStoreAddress'),
            country: Type.string,
            city: Type.string,
            postalCode: Type.string,
            line1: either(Type.string, Type.null),
            line2: either(Type.string, Type.null)
        }),
        timeZone: either(Type.string, Type.null),
        url: either(Type.string, Type.null)
    }),
    additionalTags: optional(either(Type.map(Type.string, either(Type.boolean, Type.string)), Type.null))
});

function availabilityByGeoLocation(apiUrl, query) {
    return _availabilityByGeoLocation.apply(this, arguments);
}
function _availabilityByGeoLocation() {
    _availabilityByGeoLocation = swcHelpers.asyncToGenerator(function*(apiUrl, query) {
        const filterNullable = omitBy(query, isNil);
        return validateResponse(availabilityResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/availability${query ? QueryString.stringify(filterNullable) : ''}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return _availabilityByGeoLocation.apply(this, arguments);
}
function availabilityByStoreCode(apiUrl, query) {
    return _availabilityByStoreCode.apply(this, arguments);
}
function _availabilityByStoreCode() {
    _availabilityByStoreCode = swcHelpers.asyncToGenerator(function*(apiUrl, query) {
        const filterNullable = omitBy(query, isNil);
        return validateResponse(availabilityResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/availability${query ? QueryString.stringify(filterNullable) : ''}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return _availabilityByStoreCode.apply(this, arguments);
}
/**
 * @deprecated Use availabilityByGeoLocation instead
 */ const availability = availabilityByGeoLocation;

function type(apiUrl, typeId, locale) {
    return _type.apply(this, arguments);
}
function _type() {
    _type = swcHelpers.asyncToGenerator(function*(apiUrl, typeId, locale) {
        return validateResponse(appointmentTypeResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointment-types/${typeId}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptLanguageHeader(locale), acceptJson)
        })));
    });
    return _type.apply(this, arguments);
}
function types$6(apiUrl, query, locale) {
    return _types.apply(this, arguments);
}
function _types() {
    _types = swcHelpers.asyncToGenerator(function*(apiUrl, query, locale) {
        return validateResponse(appointmentTypesResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointment-types${query ? QueryString.stringify(query) : ''}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptLanguageHeader(locale), acceptJson)
        })));
    });
    return _types.apply(this, arguments);
}

function create$3(apiUrl, request, accessToken) {
    return _create$1.apply(this, arguments);
}
function _create$1() {
    _create$1 = swcHelpers.asyncToGenerator(function*(apiUrl, request, accessToken) {
        return validateResponse(createAppointmentResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointments`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, accessToken ? authHeader(accessToken) : {}),
            body: JSON.stringify(request)
        })));
    });
    return _create$1.apply(this, arguments);
}

function get(apiUrl, appointmentId, accessToken) {
    return _get.apply(this, arguments);
}
function _get() {
    _get = swcHelpers.asyncToGenerator(function*(apiUrl, appointmentId, accessToken) {
        return validateResponse(appointmentResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointments/${appointmentId}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return _get.apply(this, arguments);
}

function cancel(apiUrl, appointmentId, accessToken, request) {
    return _cancel.apply(this, arguments);
}
function _cancel() {
    _cancel = swcHelpers.asyncToGenerator(function*(apiUrl, appointmentId, accessToken, request) {
        return validateResponse(appointmentResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointments/${appointmentId}`, swcHelpers.objectSpread({
            method: 'DELETE',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        }, request ? {
            body: JSON.stringify(request)
        } : {}))));
    });
    return _cancel.apply(this, arguments);
}

function list$5(apiUrl, accessToken, locale) {
    return _list$1.apply(this, arguments);
}
function _list$1() {
    _list$1 = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, locale) {
        return validateResponse(appointmentListResponseSpec, (yield fetch(`${apiUrl}/appointments/api/v1/appointments`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptLanguageHeader(locale), acceptJson, authHeader(accessToken))
        })));
    });
    return _list$1.apply(this, arguments);
}

var index$f = /*#__PURE__*/Object.freeze({
  __proto__: null,
  availabilityByGeoLocation: availabilityByGeoLocation,
  availabilityByStoreCode: availabilityByStoreCode,
  availability: availability,
  type: type,
  types: types$6,
  create: create$3,
  get: get,
  cancel: cancel,
  list: list$5
});

const unwrapCartResponse = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(response) {
        if (response.ok) {
            return yield response.json();
        }
        if (response.status < 500) {
            return response.json().then((error)=>{
                throw error;
            });
        }
        throw new Error(`Got non OK response from server; ${response.status} ${response.statusText}`);
    });
    return function unwrapCartResponse(response) {
        return _ref.apply(this, arguments);
    };
}();

const replicateCart = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapCartResponse((yield fetch(`${apiUrl}/replicate`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify({
                reference: request.reference
            })
        })));
    });
    return function replicateCart(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var ValidationErrorCode;
(function(ValidationErrorCode) {
    ValidationErrorCode["ContainsMutuallyExclusiveProductTypes"] = "ContainsMutuallyExclusiveProductTypes";
    ValidationErrorCode["MaxQuantitySkuExceeded"] = "MaxQuantitySkuExceeded";
    ValidationErrorCode["MaxMasterVariantQuantityExceeded"] = "MaxMasterVariantQuantityExceeded";
    ValidationErrorCode["MaxVariantsPerProductTypeQuantityExceeded"] = "MaxVariantsPerProductTypeQuantityExceeded";
    ValidationErrorCode["QuantityExceededPrescriptionExpiryDate"] = "QuantityExceededPrescriptionExpiryDate";
    ValidationErrorCode["UnavailableProductsInCart"] = "UnavailableProductsInCart";
    ValidationErrorCode["PrescriptionGlassesInvalidBundleCombination"] = "PrescriptionGlassesInvalidBundleCombination";
    ValidationErrorCode["PrescriptionGlassesInvalidOpticalParameters"] = "PrescriptionGlassesInvalidOpticalParameters";
})(ValidationErrorCode || (ValidationErrorCode = {}));

var index$e = /*#__PURE__*/Object.freeze({
  __proto__: null,
  replicateCart: replicateCart,
  get ValidationErrorCode () { return ValidationErrorCode; }
});

const integerSpec = constrain(Type.number, [
    Constraint.number.integer
]);
const positiveIntegerSpec = constrain(integerSpec, [
    Constraint.number.atLeast(0)
]);
const integerAbove0$1 = constrain(integerSpec, [
    Constraint.number.above(0)
]);
const isoDateSpec = Type.string // @TODO: Need more spec constraints
;
// copied and adapted from the obsolete dateString type https://github.com/tbknl/specified/blob/v0.4.0/src/type.ts
const dateStringSpec = {
    version: 1,
    definition: {
        type: 'dateString'
    },
    eval: (value)=>{
        const ts = Date.parse(`${value}`);
        if (ts % 1 !== 0) {
            return {
                err: {
                    code: 'type.dateString.not_valid',
                    value,
                    message: 'Not a valid date.'
                }
            };
        }
        return {
            err: null,
            value: new Date(ts)
        };
    }
};
/**
 * This constraint is copied from the customer-service, here: https://github.com/GrandVisionHQ/customer-service/blob/edd52993ff3b36fdb7472dd0eb4441fd31c41777/src/interface/models.ts#L21-L23
 * Explanation of regex: https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
 */ Constraint.string.regex(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

const addressSpec$1 = {
    key: optional(Type.string),
    title: optional(Type.string),
    salutation: optional(Type.string),
    firstName: Type.string,
    middleName: optional(Type.string),
    affixLastName: optional(Type.string),
    lastName: Type.string,
    streetName: Type.string,
    streetNumber: optional(Type.string),
    additionalStreetInfo: optional(Type.string),
    additionalAddressInfo: optional(Type.string),
    postalCode: optional(Type.string),
    city: optional(Type.string),
    county: optional(Type.string),
    region: optional(Type.string),
    state: optional(Type.string),
    country: optional(Type.string),
    company: optional(Type.string),
    department: optional(Type.string),
    building: optional(Type.string),
    apartment: optional(Type.string),
    pOBox: optional(Type.string),
    phone: optional(Type.string),
    mobile: optional(Type.string),
    email: optional(Type.string),
    externalId: optional(Type.string),
    addressTypes: Type.array(Type.literal({
        billing: 1,
        delivery: 1,
        subscription: 1,
        'billing-online': 1,
        'delivery-online': 1,
        'billing-subscription': 1,
        'delivery-subscription': 1
    }))
};
Type.interface(addressSpec$1);
const addressContainerSpec = Type.interface(swcHelpers.objectSpread({}, addressSpec$1, {
    id: Type.string,
    createdAt: optional(isoDateSpec),
    updatedAt: optional(isoDateSpec)
}));
const addressListSpec = Type.array(addressContainerSpec);

const create$2 = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { customerId , accessToken , address  }) {
        return validateResponse(addressContainerSpec, (yield fetch(`${apiUrl}/customers/customers/${encodeURIComponent(customerId)}/addresses`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(address)
        })));
    });
    return function create(apiUrl, _) {
        return _ref.apply(this, arguments);
    };
}();

const list$4 = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { customerId , accessToken  }) {
        return validateResponse(addressListSpec, (yield fetch(`${apiUrl}/customers/customers/${encodeURIComponent(customerId)}/addresses`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return function list(apiUrl, _) {
        return _ref.apply(this, arguments);
    };
}();

const deleteAddress = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { customerId , accessToken , addressId  }) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customers/${encodeURIComponent(customerId)}/addresses/${addressId}`, {
            method: 'DELETE',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return function deleteAddress(apiUrl, _) {
        return _ref.apply(this, arguments);
    };
}();

const edit = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { customerId , accessToken , address , id  }) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customers/${encodeURIComponent(customerId)}/addresses/${id}`, {
            method: 'PUT',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(address)
        })));
    });
    return function edit(apiUrl, _) {
        return _ref.apply(this, arguments);
    };
}();

var index$d = /*#__PURE__*/Object.freeze({
  __proto__: null,
  create: create$2,
  list: list$4,
  deleteAddress: deleteAddress,
  edit: edit
});

function changePassword(apiUrl, accessToken, customerId, request) {
    return _changePassword.apply(this, arguments);
}
function _changePassword() {
    _changePassword = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/account/password/${encodeURIComponent(customerId)}/change`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(request)
        })), {
            useResponseError: true,
            responseErrorMapper: (result)=>{
                return {
                    auth0ErrorCode: result === null || result === void 0 ? void 0 : result.customData.auth0ErrorCode
                };
            }
        });
    });
    return _changePassword.apply(this, arguments);
}

function resetPassword(apiUrl, request) {
    return _resetPassword.apply(this, arguments);
}
function _resetPassword() {
    _resetPassword = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/account/password/reset`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify(request)
        })));
    });
    return _resetPassword.apply(this, arguments);
}

var index$c = /*#__PURE__*/Object.freeze({
  __proto__: null,
  changePassword: changePassword,
  resetPassword: resetPassword
});

var types$5 = /*#__PURE__*/Object.freeze({
  __proto__: null
});

const prescriptionContentSpec = Type.interface({
    power: Type.string,
    baseCurve: Type.string,
    diameter: Type.string,
    cylinder: optional(Type.string),
    axis: optional(Type.string),
    addition: optional(Type.string),
    productId: optional(Type.string)
});
const prescriptionSpec = Type.interface({
    id: Type.string,
    name: Type.string,
    issueDate: isoDateSpec,
    expiryDate: isoDateSpec,
    storeName: Type.string,
    storeId: optional(Type.string),
    recommendedNextEyeTestDate: optional(isoDateSpec),
    validityStatus: Type.literal({
        VALID: 1,
        INVALID: 1
    }),
    leftEye: prescriptionContentSpec,
    rightEye: prescriptionContentSpec
});
const prescriptionsListSpec = Type.array(prescriptionSpec);

function list$3(apiUrl, accessToken, request) {
    return _list.apply(this, arguments);
}
function _list() {
    _list = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, request) {
        return validateResponse(prescriptionsListSpec, (yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(request.customerId)}/prescriptions`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return _list.apply(this, arguments);
}

var types$4 = /*#__PURE__*/Object.freeze({
  __proto__: null
});

function signedPrescriptions(apiUrl, accessToken, request) {
    return _signedPrescriptions.apply(this, arguments);
}
function _signedPrescriptions() {
    _signedPrescriptions = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(request.customerId)}/signedprescriptions`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return _signedPrescriptions.apply(this, arguments);
}

var index$b = /*#__PURE__*/Object.freeze({
  __proto__: null,
  list: list$3,
  signedPrescriptions: signedPrescriptions
});

const currencySpec = Type.literal({
    EUR: 1,
    GBP: 1
});
const moneySpec = Type.interface({
    amount: positiveIntegerSpec,
    currency: currencySpec,
    precision: integerAbove0$1
});
const deliverySpec = Type.interface({
    reference: optional(Type.string),
    date: isoDateSpec,
    boxesPerEyeSide: optional(positiveIntegerSpec),
    status: Type.literal({
        PENDING: 1,
        READY: 1,
        SHIPPED: 1,
        DELIVERED: 1
    })
});
const lineItemSpec = Type.interface({
    productId: Type.string,
    quantity: integerAbove0$1,
    application: Type.array(Type.literal({
        LEFT_EYE: 1,
        RIGHT_EYE: 1
    }))
});
const paymentMethodSpec = Type.interface({
    type: Type.string,
    details: Type.map(Type.string, Type.string)
});
const pauseOptionSpec = Type.interface({
    amount: integerSpec,
    unit: Type.literal({
        MONTHS: 1
    })
});
const subscriptionSpec = Type.interface({
    id: Type.string,
    expiryDate: isoDateSpec,
    status: Type.literal({
        ACTIVE: 1,
        PAUSED: 1,
        EXPIRED: 1,
        CANCELLED: 1,
        ON_HOLD: 1,
        PENDING: 1
    }),
    pendingChanges: Type.array(Type.literal({
        pause: 1,
        quantity: 1
    })),
    pauseOptions: Type.array(pauseOptionSpec),
    billing: Type.interface({
        totalPricePerInstallment: moneySpec
    }),
    deliveries: Type.array(deliverySpec),
    deliveryFrequency: optional(Type.literal({
        WEEKLY: 1,
        BI_WEEKLY: 1,
        MONTHLY: 1,
        QUARTERLY: 1,
        ANNUALLY: 1
    })),
    deliveryAddress: Type.map(Type.string, Type.string),
    billingAddress: optional(Type.map(Type.string, Type.string)),
    lineItems: Type.array(lineItemSpec),
    paymentMethod: paymentMethodSpec,
    topUpAllowed: Type.boolean,
    quantityChangeable: Type.boolean,
    installments: Type.array(Type.interface({
        date: isoDateSpec,
        totalPrice: moneySpec,
        paymentMethod: Type.string,
        status: Type.literal({
            PAID: 1,
            PENDING: 1,
            CANCELLED: 1,
            FAILED: 1,
            PLANNED: 1
        })
    })),
    custom: optional(Type.map(Type.string, Type.unknown))
});
const subscriptionListItemSpec = Type.interface({
    id: Type.string
});
const subscriptionList = Type.array(subscriptionListItemSpec);
const subscriptionListResponse = Type.interface({
    subscriptions: subscriptionList
});

function details$2(apiUrl, accessToken, request) {
    return _details$1.apply(this, arguments);
}
function _details$1() {
    _details$1 = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, request) {
        return validateResponse(subscriptionSpec, (yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(request.customerId)}/subscription/${request.subscriptionId}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return _details$1.apply(this, arguments);
}

function pause(apiUrl, accessToken, customerId, subscriptionId, request) {
    return _pause.apply(this, arguments);
}
function _pause() {
    _pause = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId, subscriptionId, request) {
        return okOrThrow((yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/subscription/${subscriptionId}/pause`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(request)
        })));
    });
    return _pause.apply(this, arguments);
}

function quantity(apiUrl, accessToken, customerId, subscriptionId, request) {
    return _quantity.apply(this, arguments);
}
function _quantity() {
    _quantity = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId, subscriptionId, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/subscription/${subscriptionId}/quantity`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(request)
        })));
    });
    return _quantity.apply(this, arguments);
}

var types$3 = /*#__PURE__*/Object.freeze({
  __proto__: null
});

const list$2 = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { customerId , accessToken  }) {
        return validateResponse(subscriptionListResponse, (yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/subscriptions`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return function list(apiUrl, _) {
        return _ref.apply(this, arguments);
    };
}();

var index$a = /*#__PURE__*/Object.freeze({
  __proto__: null,
  details: details$2,
  pause: pause,
  quantity: quantity,
  list: list$2
});

function changeAddress(apiUrl, accessToken, customerId, request) {
    return _changeAddress.apply(this, arguments);
}
function _changeAddress() {
    _changeAddress = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId, request) {
        var _externalId;
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/addresses`, {
            method: 'PUT',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(swcHelpers.objectSpread({}, request.fields, {
                addressTypes: request.addressTypes
            }, optionalKey('externalId', String((_externalId = request.externalId) !== null && _externalId !== void 0 ? _externalId : ''))))
        })));
    });
    return _changeAddress.apply(this, arguments);
}

const personalDetailsSchema = {
    personalDetails: Type.map(Type.string, Type.unknown)
};
const personalDetailsResponseSpec = Type.interface(personalDetailsSchema);
Type.interface(personalDetailsSchema);
const createAccountResponseSpec = Type.interface({
    accountId: Type.string,
    accountDataToken: Type.string
});

function create$1(apiUrl, request) {
    return _create.apply(this, arguments);
}
function _create() {
    _create = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { personalDetails ={} , optins ={}  } = request.additionalAccountData;
        const { loginRedirectUrl , emailAddress  } = request;
        const optInsUpdate = Object.keys(optins).length > 0 ? optins : undefined;
        const additionalData = {
            personalDetails,
            optins: optInsUpdate
        };
        const stateParams = {
            targetAction: 'signUp'
        };
        const body = swcHelpers.objectSpread({
            loginRedirectUrl,
            emailAddress,
            state: JSON.stringify(stateParams)
        }, additionalData);
        return validateResponse(createAccountResponseSpec, (yield fetch(`${apiUrl}/customers/account/create`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify(body)
        })));
    });
    return _create.apply(this, arguments);
}

function details$1(apiUrl, accessToken, customerId) {
    return _details.apply(this, arguments);
}
function _details() {
    _details = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId) {
        return validateResponse(personalDetailsResponseSpec, (yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/details`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return _details.apply(this, arguments);
}
function updateDetails(apiUrl, accessToken, customerId, request) {
    return _updateDetails.apply(this, arguments);
}
function _updateDetails() {
    _updateDetails = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, customerId, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customers/customer/${encodeURIComponent(customerId)}/details`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken)),
            body: JSON.stringify(request)
        })));
    });
    return _updateDetails.apply(this, arguments);
}

var FormName;
(function(FormName) {
    FormName["personalDetails"] = "personalDetails";
    FormName["billingAddress"] = "billingAddress";
    FormName["preferredStore"] = "preferredStore";
})(FormName || (FormName = {}));

var index$9 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Addresses: index$d,
  Password: index$c,
  Subscriptions: index$a,
  Prescriptions: index$b,
  PasswordTypes: types$5,
  SubscriptionsTypes: types$3,
  PrescriptionsTypes: types$4,
  changeAddress: changeAddress,
  create: create$1,
  details: details$1,
  updateDetails: updateDetails,
  get FormName () { return FormName; }
});

const placePredictionsResponseSpec = Type.interface({
    name: Type.string,
    places: Type.array(Type.array(Type.string))
});
const placePredictionsExtendedResponseSpec = Type.interface({
    name: Type.string,
    places: Type.array(Type.interface({
        id: Type.string,
        address: Type.array(Type.string)
    }))
});
const placeDetailsResponseSpec = Type.interface({
    coordinates: Type.interface({
        lat: Type.number,
        lng: Type.number
    })
});
const coordinatesResponseSpec = Type.interface({
    address: Type.string,
    coordinates: Type.interface({
        lat: Type.number,
        lng: Type.number
    })
});

const getCoordinates = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return validateResponse(coordinatesResponseSpec, (yield fetch(`${apiUrl}/address/coordinates?address=${request.query}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getCoordinates(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getPlaceDetails = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return validateResponse(placeDetailsResponseSpec, (yield fetch(`${apiUrl}/place/details?place_id=${request.id}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getPlaceDetails(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getPlacePredictions = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return validateResponse(placePredictionsResponseSpec, (yield fetch(`${apiUrl}/place/predictions?name=${request.place}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getPlacePredictions(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getPlacePredictionsExtended = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return validateResponse(placePredictionsExtendedResponseSpec, (yield fetch(`${apiUrl}/place/predictions/extended?name=${request.place}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getPlacePredictionsExtended(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var index$8 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getCoordinates: getCoordinates,
  getPlaceDetails: getPlaceDetails,
  getPlacePredictions: getPlacePredictions,
  getPlacePredictionsExtended: getPlacePredictionsExtended
});

function decide(apiUrl, request) {
    return _decide.apply(this, arguments);
}
function _decide() {
    _decide = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { experiment , userId  } = request;
        const url = new URL(`experiments/${experiment}`, apiUrl);
        return new Promise(function() {
            var _ref = swcHelpers.asyncToGenerator(function*(resolve, reject) {
                try {
                    var ref;
                    const response = yield fetch(url.href, {
                        method: 'POST',
                        body: JSON.stringify({
                            userId
                        }),
                        headers: swcHelpers.objectSpread({}, postJson, acceptJson)
                    });
                    const body = yield response.json();
                    // If Optimizely is not configured for an experiment fetch won't throw,
                    // but we should consider it an error case so we check the response message here
                    // and reject if it has an error messsage
                    if ((body === null || body === void 0 ? void 0 : body.error) && (body === null || body === void 0 ? void 0 : (ref = body.error) === null || ref === void 0 ? void 0 : ref.message)) {
                        return reject(new Error(body.error.message));
                    }
                    resolve(body);
                } catch (error) {
                    reject(error);
                }
            });
            return function(resolve, reject) {
                return _ref.apply(this, arguments);
            };
        }());
    });
    return _decide.apply(this, arguments);
}

var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  decide: decide
});

const create = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request, accessToken) {
        return unwrapResponse((yield fetch(apiUrl, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, accessToken ? authHeader(accessToken) : {}, correlationHeader()),
            body: JSON.stringify(request)
        })));
    });
    return function create(apiUrl, request, accessToken) {
        return _ref.apply(this, arguments);
    };
}();
const createForCustomer = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, customerId, accessToken, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/customer/${encodeURIComponent(customerId)}/create`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson, authHeader(accessToken), correlationHeader()),
            body: JSON.stringify(request)
        })));
    });
    return function createForCustomer(apiUrl, customerId, accessToken, request) {
        return _ref.apply(this, arguments);
    };
}();

const integerAbove0 = constrain(integerSpec, [
    Constraint.number.above(0)
]);
const localizedStringSpec$1 = adjust(Type.map(Type.string, Type.string), {
    skipInvalidValues: true
});
const addressSpec = adjust(Type.map(Type.string, Type.string), {
    skipInvalidValues: true
});
const money = Type.interface({
    currency: Type.string,
    amount: positiveIntegerSpec,
    precision: integerAbove0
});
const mediaImages = Type.interface({
    url: Type.string,
    dimensions: Type.interface({
        w: Type.number,
        h: Type.number
    }),
    label: optional(Type.string)
});
const productSchemaBase = {
    name: localizedStringSpec$1,
    sku: Type.string,
    imageId: optional(Type.string),
    media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
};
const attributesSchemaBase = {
    brand: Type.string,
    brandModel: optional(Type.string),
    brandModelCode: optional(Type.string),
    opcoBackendId: optional(Type.string),
    supplierEAN: optional(Type.string)
};
const contactLensAttributeTypes = either(Type.literal({
    'Contact-lenses': 1
}), Type.literal({
    CONTACT_LENSES: 1
}));
const contactLensBundleAttributeTypes = either(Type.literal({
    'Contact-lens-bundles': 1
}), Type.literal({
    CONTACT_LENS_BUNDLES: 1
}));
const contactLensesAttributesSchemaBase = {
    packageQuantity: positiveIntegerSpec,
    replacementFrequency: localizedStringSpec$1,
    eyeSide: optional(Type.literal({
        left: 1,
        right: 1,
        both: 1
    })),
    isSubscription: optional(Type.boolean),
    // temporary property, needs be removed when LiquidPixel is replaced with Bynder
    sapArticleNumber: optional(Type.string)
};
const contactLensesProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(contactLensesAttributesSchemaBase)
}));
const contactLensesBundleAttributesSchemaBase = {
    lensType: localizedStringSpec$1,
    isSubscription: Type.boolean,
    months: positiveIntegerSpec,
    imageSku: optional(Type.string),
    fixedDiscount: optional(Type.number),
    leftLensAxis: optional(Type.number),
    leftLensBaseCurve: optional(Type.number),
    leftLensCylinder: optional(Type.number),
    leftLensDiameter: optional(Type.number),
    leftLensName: optional(Type.string),
    leftLensPower: optional(Type.number),
    leftLensSku: optional(Type.string),
    leftLensVariantSku: optional(Type.string),
    rightLensAxis: optional(Type.number),
    rightLensBaseCurve: optional(Type.number),
    rightLensCylinder: optional(Type.number),
    rightLensDiameter: optional(Type.number),
    rightLensName: optional(Type.string),
    rightLensPower: optional(Type.number),
    rightLensSku: optional(Type.string),
    rightLensVariantSku: optional(Type.string),
    solutionName: optional(Type.string),
    solutionSku: optional(Type.string),
    bundleProduct1Media: optional(Type.interface({
        images: Type.array(mediaImages)
    })),
    bundleProduct2Media: optional(Type.interface({
        images: Type.array(mediaImages)
    })),
    bundleProduct3Media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
};
const contactLensesBundleSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensBundleAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(contactLensesBundleAttributesSchemaBase)
}));
/*
  The baseCurve, diameter and power parameters are temporarily made optional due to the issue
  with order-service. Needs to be reverted later
  See the issue: https://jira.grandvision.global/browse/MACC-3839
*/ const contactLensesProductDetailsSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, contactLensesAttributesSchemaBase, {
        baseCurve: optional(Type.string),
        diameter: optional(Type.string),
        power: optional(Type.string),
        axis: optional(Type.string),
        cylinder: optional(Type.string),
        addition: optional(Type.string)
    }))
}));
const solutionAttributeTypes = either(Type.literal({
    Solutions: 1
}), Type.literal({
    SOLUTIONS: 1
}));
const solutionProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: solutionAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface({
        brand: attributesSchemaBase.brand,
        solutionType: optional(Type.string),
        volume: positiveIntegerSpec,
        opcoBackendId: optional(Type.string)
    })
}));
const sunglassesAttributeTypes = either(Type.literal({
    sunglasses: 1
}), Type.literal({
    SUNGLASSES: 1
}));
const sunglassesSchema = swcHelpers.objectSpread({
    attributesType: sunglassesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const sunglassesProductSpec = Type.interface(sunglassesSchema);
const prescriptionGlassesAttributeTypes = either(Type.literal({
    prescriptionGlasses: 1
}), Type.literal({
    PRESCRIPTION_GLASSES: 1
}));
const Prescription = adjust(Type.map(Type.string, Type.number), {
    skipInvalidValues: true
});
const PrescriptionGlassesMoney = Type.interface({
    currencyCode: Type.string,
    centAmount: Type.number,
    fractionDigits: Type.number,
    type: Type.string
});
const readyReadersAttributeTypes = either(Type.literal({
    readyReaders: 1
}), Type.literal({
    READY_READERS: 1
}));
const readyReadersSchema = swcHelpers.objectSpread({
    attributesType: readyReadersAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const readyReadersProductSpec = Type.interface(readyReadersSchema);
const BundlePrice = Type.interface({
    id: Type.string,
    value: PrescriptionGlassesMoney,
    discounted: optional(Type.interface({
        value: PrescriptionGlassesMoney
    }))
});
const variant = Type.interface({
    sku: Type.string,
    media: optional(Type.interface({
        images: Type.array(mediaImages)
    }))
});
const prescriptionMetaDataSchema = {
    expiryDate: optional(isoDateSpec),
    opticianDetails: optional(Type.string)
};
const prescriptionDataSchema = swcHelpers.objectSpread({}, prescriptionMetaDataSchema, {
    rightLens: optional(Prescription),
    leftLens: optional(Prescription)
});
Type.interface(prescriptionDataSchema);
const existingCustomer = Type.interface({
    type: Type.literal({
        ExistingCustomer: 1
    })
});
const newPrescription = Type.interface(swcHelpers.objectSpread({
    type: Type.literal({
        NewPrescription: 1
    })
}, prescriptionDataSchema));
const prescriptionFile = Type.interface({
    type: Type.literal({
        PrescriptionFile: 1
    }),
    fileName: Type.string,
    storageId: Type.string,
    pupillaryDistanceFileFileName: Type.string,
    pupillaryDistanceFileStorageId: Type.string
});
const emailPrescription = Type.interface({
    type: Type.literal({
        EmailPrescription: 1
    })
});
const bundleChildLineItemBase = {
    productId: Type.string,
    productType: Type.string,
    name: localizedStringSpec$1,
    price: BundlePrice
};
const bundleMainLineItemBase = {
    price: BundlePrice,
    productType: Type.string,
    opcoBackendProductId: optional(Type.string)
};
const bundleChildLineItem = Type.interface(swcHelpers.objectSpread({}, bundleChildLineItemBase, {
    variant
}));
const bundleMainLineItem = Type.interface(swcHelpers.objectSpread({}, bundleMainLineItemBase, {
    variant
}));
const prescriptionGlassesProductSpec = Type.interface(swcHelpers.objectSpread({}, sunglassesSchema, {
    attributesType: prescriptionGlassesAttributeTypes,
    lineItemDetail: Type.interface({
        bundleMainLineItem,
        bundleChildLineItems: Type.array(bundleChildLineItem),
        prescription: optional(either(existingCustomer, newPrescription, prescriptionFile, emailPrescription))
    })
}));
const nonPrescriptionBundlesTypes = either(Type.literal({
    nonPrescriptionBundle: 1
}), Type.literal({
    NON_PRESCRIPTION_BUNDLE: 1
}));
const nonPrescriptionBundlesSchema = swcHelpers.objectSpread({
    attributesType: nonPrescriptionBundlesTypes
}, productSchemaBase, {
    attributes: Type.interface({
        includesLenses: Type.boolean
    }),
    lineItemDetail: Type.interface({
        bundleMainLineItem: Type.interface(bundleMainLineItemBase),
        bundleChildLineItems: Type.array(Type.interface(bundleChildLineItemBase)),
        configurationBundle: Type.interface(swcHelpers.objectSpread({}, bundleChildLineItemBase, {
            isPrescriptionNeeded: Type.boolean,
            includesLenses: Type.boolean
        }))
    })
});
const nonPrescriptionBundleProductSpec = Type.interface(nonPrescriptionBundlesSchema);
const framesAttributeTypes = either(Type.literal({
    frames: 1
}), Type.literal({
    FRAMES: 1
}));
const framesProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: framesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
}));
const clsPriceSpec = Type.interface({
    discount: Type.interface({
        amount: Type.number,
        percentage: Type.number
    }),
    net: Type.number,
    subtotal: Type.number,
    tax: Type.interface({
        amount: Type.number,
        portions: Type.array(Type.interface({
            rate: Type.number,
            amount: Type.number
        }))
    }),
    gross: Type.number
});
const contactLensSubscriptionItem = Type.interface({
    gan: Type.string,
    mediaImages: Type.array(mediaImages),
    name: Type.string,
    packageQuantity: optional(Type.number),
    quantity: Type.number,
    ratePlanId: Type.string,
    regularImages: Type.array(mediaImages),
    sku: Type.string,
    type: Type.literal({
        SOLUTION: 1,
        LENS: 1
    }),
    price: Type.interface({
        total: clsPriceSpec,
        unit: clsPriceSpec
    })
});
const lensPrescription = Type.interface({
    gan: Type.string,
    sku: Type.string,
    baseCurve: Type.number,
    diameter: Type.number,
    power: Type.number,
    axis: optional(Type.number),
    cylinder: optional(Type.number),
    addition: optional(Type.string)
});
const contactLensSubscriptionAttributeTypes = either(Type.literal({
    contactLensSubscription: 1
}), Type.literal({
    CONTACT_LENS_SUBSCRIPTION: 1
}));
const contactLensSubscriptionProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: contactLensSubscriptionAttributeTypes
}, productSchemaBase, {
    lineItemDetail: Type.interface({
        bundleLineItems: Type.array(contactLensSubscriptionItem)
    }),
    typeDetails: Type.interface({
        billingFrequency: Type.literal({
            MONTHLY: 1,
            QUARTERLY: 1
        }),
        deliveryFrequency: Type.literal({
            MONTHLY: 1,
            QUARTERLY: 1
        }),
        subscriptionStartDate: Type.string,
        prescription: Type.interface({
            leftLens: optional(lensPrescription),
            rightLens: optional(lensPrescription)
        })
    })
}));
const accessoriesAttributeTypes = either(Type.literal({
    accessories: 1
}), Type.literal({
    ACCESSORIES: 1
}));
const accessoriesSchema = swcHelpers.objectSpread({
    attributesType: accessoriesAttributeTypes
}, productSchemaBase, {
    attributes: Type.interface(swcHelpers.objectSpread({}, attributesSchemaBase))
});
const accessoriesProductSpec = Type.interface(accessoriesSchema);
const otherProductSpec = Type.interface(swcHelpers.objectSpread({
    attributesType: optional(Type.literal({}))
}, productSchemaBase, {
    attributes: Type.interface({})
}));
swcHelpers.objectSpread({}, accessoriesAttributeTypes, contactLensAttributeTypes, contactLensSubscriptionAttributeTypes, solutionAttributeTypes, sunglassesAttributeTypes, framesAttributeTypes, contactLensBundleAttributeTypes, prescriptionGlassesAttributeTypes, readyReadersAttributeTypes, nonPrescriptionBundlesTypes);
const lineItemSchemaBase = {
    catalogPrice: money,
    discountedPrice: optional(money),
    totalPrice: optional(money),
    includedDiscountDescription: optional(localizedStringSpec$1),
    quantity: integerAbove0
};
Type.interface(swcHelpers.objectSpread({
    product: nonPrescriptionBundleProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: prescriptionGlassesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensesBundleSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: solutionProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: framesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: sunglassesProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: contactLensSubscriptionProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: otherProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: readyReadersProductSpec
}, lineItemSchemaBase));
Type.interface(swcHelpers.objectSpread({
    product: accessoriesProductSpec
}, lineItemSchemaBase));
const historyLineItem = Type.interface(swcHelpers.objectSpread({
    product: either(accessoriesProductSpec, contactLensesProductSpec, contactLensesBundleSpec, solutionProductSpec, framesProductSpec, sunglassesProductSpec, nonPrescriptionBundleProductSpec, prescriptionGlassesProductSpec, contactLensSubscriptionProductSpec, otherProductSpec, readyReadersProductSpec)
}, lineItemSchemaBase));
const detailsLineItem = Type.interface(swcHelpers.objectSpread({
    product: either(accessoriesProductSpec, contactLensesProductDetailsSpec, contactLensesBundleSpec, solutionProductSpec, framesProductSpec, sunglassesProductSpec, nonPrescriptionBundleProductSpec, prescriptionGlassesProductSpec, contactLensSubscriptionProductSpec, otherProductSpec, readyReadersProductSpec)
}, lineItemSchemaBase));
const orderBaseSchema = {
    provider: Type.string,
    orderNumber: constrain(Type.string, [
        Constraint.string.regex(/^[0-9]+$/)
    ]),
    orderDate: dateStringSpec,
    shipmentState: optional(Type.literal({
        Shipped: 1,
        Ready: 1,
        Pending: 1,
        Delayed: 1,
        Partial: 1,
        Backorder: 1
    })),
    paymentMethod: optional(Type.literal({
        bancontactCard: 1,
        bancontactMobile: 1,
        cashOnDelivery: 1,
        creditcard: 1,
        fake: 1,
        ideal: 1,
        klarna_account: 1,
        klarna: 1,
        mbway: 1,
        mobilepay: 1,
        multibanco: 1,
        paypal: 1,
        sepadirectdebit: 1,
        oney: 1,
        swish: 1
    })),
    paymentState: optional(Type.literal({
        BalanceDue: 1,
        CreditOwed: 1,
        Failed: 1,
        Paid: 1,
        Pending: 1
    })),
    totalChargedPrice: money,
    trackingURL: optional(Type.string)
};
const orderHistoryItemSpec = Type.interface(swcHelpers.objectSpread({}, orderBaseSchema, {
    lineItems: Type.array(historyLineItem)
}));
const orderHistoryResponseSpec = Type.interface({
    orders: Type.array(orderHistoryItemSpec),
    total: positiveIntegerSpec
});
const orderDetailsItemSpec = Type.interface(swcHelpers.objectSpread({}, orderBaseSchema, {
    lineItems: Type.array(detailsLineItem),
    totalProductsPrice: money,
    shippingPrice: money,
    totalAdditionalDiscount: money,
    shippingAddress: addressSpec,
    billingAddress: addressSpec,
    shippingInfo: Type.interface({
        deliveryMethod: Type.string
    }),
    customerInfo: Type.interface({
        email: Type.string,
        phone: optional(Type.string)
    })
}));
const orderDetailsResponseSpec = Type.interface({
    order: orderDetailsItemSpec
});

const details = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, request) {
        return validateResponse(orderDetailsResponseSpec, (yield fetch(`${apiUrl}/customer/${request.customerId}/details/${request.orderId}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return function details(apiUrl, accessToken, request) {
        return _ref.apply(this, arguments);
    };
}();

// Deprecated types
const DEPRECATED_FRAMES_TYPE = 'frames';
const DEPRECATED_CONTACT_LENSES_TYPE = 'Contact-lenses';
const DEPRECATED_CONTACT_LENS_BUNDLE_TYPE = 'Contact-lens-bundles';
const DEPRECATED_SUNGLASSES_TYPE = 'sunglasses';
const DEPRECATED_SOLUTION_TYPE = 'Solutions';
const DEPRECATED_NON_PRESCRIPTION_BUNDLE = 'nonPrescriptionBundle';
const DEPRECATED_PRESCRIPTION_GLASSES = 'prescriptionGlasses';
// This one isn't updated in the backend yet but should be upper case eventually
const DEPRECATED_CONTACT_LENS_SUBSCRIPTION_TYPE = 'contactLensSubscription';
// New unified attribute types
const FRAMES_TYPE = 'FRAMES';
const CONTACT_LENSES_TYPE = 'CONTACT_LENSES';
const CONTACT_LENS_BUNDLE_TYPE = 'CONTACT_LENS_BUNDLES';
const SUNGLASSES_TYPE = 'SUNGLASSES';
const SOLUTION_TYPE = 'SOLUTIONS';
const NON_PRESCRIPTION_BUNDLE = 'NON_PRESCRIPTION_BUNDLE';
const PRESCRIPTION_GLASSES = 'PRESCRIPTION_GLASSES';
const CONTACT_LENS_SUBSCRIPTION_TYPE = 'CONTACT_LENS_SUBSCRIPTION';
const READY_READERS_TYPE = 'READY_READERS';
const ACCESSORIES_TYPE = 'ACCESSORIES';
const isSubscriptionOrderLineItem = (orderLineItem)=>isSubscriptionProduct(orderLineItem.product)
;
const isSubscriptionProduct = (product)=>product.attributesType === CONTACT_LENS_SUBSCRIPTION_TYPE || product.attributesType === DEPRECATED_CONTACT_LENS_SUBSCRIPTION_TYPE
;
const isContactLenses = (product)=>product.attributesType === CONTACT_LENSES_TYPE || product.attributesType === DEPRECATED_CONTACT_LENSES_TYPE
;
const isContactLensesLineItem = (orderLineItem)=>isContactLenses(orderLineItem.product)
;
const isContactLensesSubscriptionLineItem = (orderLineItem)=>isContactLensesSubscription(orderLineItem.product)
;
const isContactLensBundleLineItem = (orderLineItem)=>isContactLensesBundle(orderLineItem.product)
;
const isContactLensesSubscription = (product)=>product.attributesType === CONTACT_LENS_SUBSCRIPTION_TYPE || product.attributesType === DEPRECATED_CONTACT_LENS_SUBSCRIPTION_TYPE
;
const isFrames = (product)=>product.attributesType === FRAMES_TYPE || product.attributesType === DEPRECATED_FRAMES_TYPE
;
const isContactLensesBundle = (product)=>product.attributesType === CONTACT_LENS_BUNDLE_TYPE || product.attributesType === DEPRECATED_CONTACT_LENS_BUNDLE_TYPE
;
const isDetailContactLenses = (product)=>product.attributesType === CONTACT_LENSES_TYPE || product.attributesType === DEPRECATED_CONTACT_LENSES_TYPE
;
const isDetailContactLensesSubscriptionProduct = (product)=>product.attributesType === CONTACT_LENS_SUBSCRIPTION_TYPE || product.attributesType === DEPRECATED_CONTACT_LENS_SUBSCRIPTION_TYPE
;
const isDetailContactLensesSubscriptionLineItem = (lineItem)=>isDetailContactLensesSubscriptionProduct(lineItem.product)
;
const isPrescriptionGlasses = (product)=>product.attributesType === PRESCRIPTION_GLASSES || product.attributesType === DEPRECATED_PRESCRIPTION_GLASSES
;
const isNonPrescriptionBundle = (product)=>product.attributesType === DEPRECATED_NON_PRESCRIPTION_BUNDLE || product.attributesType === NON_PRESCRIPTION_BUNDLE
;
const isPrescriptionGlassesLineItem = (lineItem)=>isPrescriptionGlasses(lineItem.product)
;
const isNonPrescriptionBundleLineItem = (lineItem)=>isNonPrescriptionBundle(lineItem.product)
;
const isSolutionLineItem = (lineItem)=>isSolution(lineItem.product)
;
const isPrescriptionBundleInArray = (items)=>items.some((item)=>isPrescriptionGlasses(item.product)
    )
;
const isSubscriptionItemInArray = (items)=>items.some((item)=>isContactLensesSubscription(item.product)
    )
;
const isNonPrescriptionBundleInArray = (items)=>items.some((item)=>isNonPrescriptionBundle(item.product)
    )
;
const isDetailContactLensBundles = (product)=>product.attributesType === CONTACT_LENS_BUNDLE_TYPE || product.attributesType === DEPRECATED_CONTACT_LENS_BUNDLE_TYPE
;
const isSunglassesLineItem = (orderLineItem)=>orderLineItem.product.attributesType === SUNGLASSES_TYPE || orderLineItem.product.attributesType === DEPRECATED_SUNGLASSES_TYPE
;
const isSunglasses = (product)=>product.attributesType === SUNGLASSES_TYPE || product.attributesType === DEPRECATED_SUNGLASSES_TYPE
;
const isSolution = (product)=>product.attributesType === SOLUTION_TYPE || product.attributesType === DEPRECATED_SOLUTION_TYPE
;
const isOther = (product)=>product.attributesType === undefined || !CONTACT_LENSES_TYPE || !SOLUTION_TYPE
;
const isReadyReadersLineItem = (orderLineItem)=>orderLineItem.product.attributesType === READY_READERS_TYPE
;
const isReadyReaders = (product)=>product.attributesType === READY_READERS_TYPE
;
const isAccessoriesLineItem = (orderLineItem)=>orderLineItem.product.attributesType === ACCESSORIES_TYPE
;
const isAccessories = (product)=>product.attributesType === ACCESSORIES_TYPE
;

const list$1 = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, accessToken, request) {
        return validateResponse(orderHistoryResponseSpec, (yield fetch(`${apiUrl}/customer/${request.customerId}/history?offset=${request.offset}&limit=${request.limit}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, authHeader(accessToken))
        })));
    });
    return function list(apiUrl, accessToken, request) {
        return _ref.apply(this, arguments);
    };
}();

const getOrderByToken = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { orderData  } = request;
        return unwrapResponse((yield fetch(`${apiUrl}/${orderData}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getOrderByToken(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var PaymentState;
(function(PaymentState) {
    PaymentState["Paid"] = "Paid";
    PaymentState["Pending"] = "Pending";
    PaymentState["Failed"] = "Failed";
    PaymentState["CreditOwed"] = "CreditOwed";
    PaymentState["BalanceDue"] = "BalanceDue";
})(PaymentState || (PaymentState = {}));
const getPaymentState = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/order/${request.orderId}/payment-state`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getPaymentState(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const unlink = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { orderNumber , orderToken  } = request;
        const response = yield fetch(`${apiUrl}/orders/${orderNumber}/customer?orderDataToken=${orderToken}`, {
            method: 'DELETE',
            headers: swcHelpers.objectSpread({}, acceptJson)
        });
        okOrThrow(response);
        return response;
    });
    return function unlink(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  create: create,
  createForCustomer: createForCustomer,
  details: details,
  isSubscriptionOrderLineItem: isSubscriptionOrderLineItem,
  isSubscriptionProduct: isSubscriptionProduct,
  isContactLenses: isContactLenses,
  isContactLensesLineItem: isContactLensesLineItem,
  isContactLensesSubscriptionLineItem: isContactLensesSubscriptionLineItem,
  isContactLensBundleLineItem: isContactLensBundleLineItem,
  isContactLensesSubscription: isContactLensesSubscription,
  isFrames: isFrames,
  isContactLensesBundle: isContactLensesBundle,
  isDetailContactLenses: isDetailContactLenses,
  isDetailContactLensesSubscriptionProduct: isDetailContactLensesSubscriptionProduct,
  isDetailContactLensesSubscriptionLineItem: isDetailContactLensesSubscriptionLineItem,
  isPrescriptionGlasses: isPrescriptionGlasses,
  isNonPrescriptionBundle: isNonPrescriptionBundle,
  isPrescriptionGlassesLineItem: isPrescriptionGlassesLineItem,
  isNonPrescriptionBundleLineItem: isNonPrescriptionBundleLineItem,
  isSolutionLineItem: isSolutionLineItem,
  isPrescriptionBundleInArray: isPrescriptionBundleInArray,
  isSubscriptionItemInArray: isSubscriptionItemInArray,
  isNonPrescriptionBundleInArray: isNonPrescriptionBundleInArray,
  isDetailContactLensBundles: isDetailContactLensBundles,
  isSunglassesLineItem: isSunglassesLineItem,
  isSunglasses: isSunglasses,
  isSolution: isSolution,
  isOther: isOther,
  isReadyReadersLineItem: isReadyReadersLineItem,
  isReadyReaders: isReadyReaders,
  isAccessoriesLineItem: isAccessoriesLineItem,
  isAccessories: isAccessories,
  list: list$1,
  getOrderByToken: getOrderByToken,
  get PaymentState () { return PaymentState; },
  getPaymentState: getPaymentState,
  unlink: unlink
});

const completeAdyenPayment = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { details , paymentData , paymentId  } = request;
        return unwrapResponse((yield fetch(`${apiUrl}/payments/adyen/complete`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify({
                details,
                paymentData,
                paymentId
            })
        })));
    });
    return function completeAdyenPayment(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();
const completeAdyenRedirectPayment = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { details , paymentData , paymentId  } = request;
        return unwrapResponse((yield fetch(`${apiUrl}/payments/adyen/complete`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify({
                details,
                paymentData,
                paymentId
            })
        })));
    });
    return function completeAdyenRedirectPayment(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();
const completeAdyen3DSecurePayment = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { details , paymentData , paymentId  } = request;
        return unwrapResponse((yield fetch(`${apiUrl}/payments/adyen/complete`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify({
                details,
                paymentData,
                paymentId
            })
        })));
    });
    return function completeAdyen3DSecurePayment(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  completeAdyenPayment: completeAdyenPayment,
  completeAdyenRedirectPayment: completeAdyenRedirectPayment,
  completeAdyen3DSecurePayment: completeAdyen3DSecurePayment
});

const getPaymentMethods = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const { countryCode , shippingMethod , cartId  } = request;
        return unwrapResponse((yield fetch(`${apiUrl}/paymentMethods`, {
            method: 'POST',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson),
            body: JSON.stringify({
                countryCode,
                shippingMethod,
                cartId
            })
        })));
    });
    return function getPaymentMethods(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var PaymentMethod;
(function(PaymentMethod) {
    PaymentMethod["BancontactCard"] = 'bancontactCard';
    PaymentMethod["BancontactMobile"] = 'bancontactMobile';
    PaymentMethod["CashOnDelivery"] = 'cashOnDelivery';
    PaymentMethod["Creditcard"] = 'creditcard';
    PaymentMethod["Fake"] = 'fake';
    PaymentMethod["Ideal"] = 'ideal';
    PaymentMethod["Klarna"] = 'klarna';
    PaymentMethod["KlarnaAccount"] = 'klarna_account';
    PaymentMethod["MBway"] = 'mbway';
    PaymentMethod["MobilePay"] = 'mobilepay';
    PaymentMethod["Multibanco"] = 'multibanco';
    PaymentMethod["Oney"] = 'oney';
    PaymentMethod["Paypal"] = 'paypal';
    PaymentMethod["SepaDirectDebit"] = 'sepadirectdebit';
    PaymentMethod["Swish"] = 'swish';
    PaymentMethod["ApplePay"] = 'applepay';
})(PaymentMethod || (PaymentMethod = {}));
var PaymentServiceProvider;
(function(PaymentServiceProvider) {
    PaymentServiceProvider["Adyen"] = 'adyen';
    PaymentServiceProvider["Fake"] = 'fake';
    PaymentServiceProvider["CashOnDelivery"] = 'cashOnDelivery';
})(PaymentServiceProvider || (PaymentServiceProvider = {}));
var PaymentResult;
(function(PaymentResult) {
    PaymentResult["Success"] = 'success';
    PaymentResult["Approved"] = 'approved';
    PaymentResult["Pending"] = 'pending';
    PaymentResult["Failed"] = 'failed';
    PaymentResult["Redirect"] = 'redirect';
    PaymentResult["Error"] = 'error';
    PaymentResult["Cancelled"] = 'cancelled';
    PaymentResult["Refused"] = 'refused';
    PaymentResult["InternalError"] = 'internal-error';
    PaymentResult["Received"] = 'received';
})(PaymentResult || (PaymentResult = {}));
var Gender;
(function(Gender) {
    Gender["Male"] = 'MALE';
    Gender["Female"] = 'FEMALE';
    Gender["Unknown"] = 'UNKNOWN';
})(Gender || (Gender = {}));
var CartState;
(function(CartState) {
    CartState["Active"] = "Active";
    CartState["Merged"] = "Merged";
    CartState["Ordered"] = "Ordered";
})(CartState || (CartState = {}));
var CallbackResultCode;
(function(CallbackResultCode) {
    CallbackResultCode["Authorised"] = 'authorised';
    CallbackResultCode["Cancelled"] = 'cancelled';
    CallbackResultCode["Refused"] = 'refused';
    CallbackResultCode["Pending"] = 'pending';
    CallbackResultCode["Received"] = 'received';
})(CallbackResultCode || (CallbackResultCode = {}));

var types$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get PaymentMethod () { return PaymentMethod; },
  get PaymentServiceProvider () { return PaymentServiceProvider; },
  get PaymentResult () { return PaymentResult; },
  get CartState () { return CartState; },
  get CallbackResultCode () { return CallbackResultCode; }
});

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Adyen: index$5,
  getPaymentMethods: getPaymentMethods,
  get PaymentMethod () { return PaymentMethod; },
  get PaymentServiceProvider () { return PaymentServiceProvider; },
  get PaymentResult () { return PaymentResult; },
  get CartState () { return CartState; },
  get CallbackResultCode () { return CallbackResultCode; }
});

const query = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const category = request && request.categoryType || undefined;
        const expand = request && request.expand ? request.expand.join('|') : undefined;
        const facets = request && request.facets ? request.facets.join('|') : undefined;
        const filters = request && request.filters ? request.filters.join('|') : undefined;
        const pageIndex = request && request.pageIndex || 1;
        const pageSize = request && request.pageSize || 24;
        const sortBy = request && request.sortBy || 'price';
        const sortDirection = request && request.sortDirection || 'asc';
        const queryParams = QueryString.stringify({
            expand,
            filters,
            facets,
            pageIndex,
            pageSize,
            sortBy,
            sortDirection
        }, true, '&', '=', false);
        const endpoint = category ? `/category/${category}/` : '/';
        return unwrapResponse((yield fetch(`${apiUrl}${endpoint}?${queryParams}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function query(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getProductById = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const expand = request.expand ? request.expand.join('|') : null;
        return unwrapResponse((yield fetch(`${apiUrl}/${request.productId}?expand=${expand}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function getProductById(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();
const getProductBySlug = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const expands = request.expand || [
            'attributes',
            'tax-category',
            'discounts',
            'categories',
            'product-type',
            'review-enabled', 
        ];
        return unwrapResponse((yield fetch(`${apiUrl}/slug/${request.slug}?expand=${expands.join('|')}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function getProductBySlug(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getCategoryByKey = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/categories/${request.key}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function getCategoryByKey(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getContactLenses = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/contactlenses/${request.variantGroup}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function getContactLenses(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();
const getContactLensBundles = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        const expands = [
            'masterVariant.prices[*].discounted.discount',
            'variants[*].prices[*].discounted.discount',
            'masterVariant.attributes[*].value',
            'productType', 
        ];
        const queryParams = QueryString.stringify({
            expand: expands.join('|'),
            filters: `variants.sku:"${request.sku}"`,
            solutions: true
        }, true, '&', '=', false);
        return unwrapResponse((yield fetch(`${apiUrl}/?${queryParams}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson, postJson)
        })));
    });
    return function getContactLensBundles(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  query: query,
  getProductById: getProductById,
  getProductBySlug: getProductBySlug,
  getCategoryByKey: getCategoryByKey,
  getContactLenses: getContactLenses,
  getContactLensBundles: getContactLensBundles
});

function getVersion(apiUrl) {
    return _getVersion.apply(this, arguments);
}
function _getVersion() {
    _getVersion = swcHelpers.asyncToGenerator(function*(apiUrl) {
        const url = new URL('status/versions', apiUrl);
        return unwrapResponse((yield fetch(url.href)), {
            useResponseError: true
        });
    });
    return _getVersion.apply(this, arguments);
}

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getVersion: getVersion
});

const localizedStringSpec = Type.map(Type.string, Type.string);
const townItem = Type.interface({
    town: Type.string,
    townLocalized: optional(localizedStringSpec)
});
const townsResponseSpec = Type.array(townItem);
const storeSitemapItem = Type.interface({
    code: Type.number,
    slug: optional(Type.string),
    slugLocalized: optional(localizedStringSpec),
    town: Type.string,
    townLocalized: optional(localizedStringSpec)
});
const storeSitemapResponseSpec = Type.array(storeSitemapItem);

const getTowns = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl) {
        return validateResponse(townsResponseSpec, (yield fetch(`${apiUrl}/towns`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getTowns(apiUrl) {
        return _ref.apply(this, arguments);
    };
}();

/**
 * Lists all stores for an opco
 */ const list = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl) {
        return unwrapResponse((yield fetch(`${apiUrl}/list`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function list(apiUrl) {
        return _ref.apply(this, arguments);
    };
}();
/**
 * Retrieve details of a single store using the store code.
 */ const getDetailsByCode = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/${request.code}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getDetailsByCode(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();
/**
 * Function to retrieve a list of stores based on a filter parameter
 * Currently only supports slug lookup
 */ const getStoresByFilter = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, request) {
        return unwrapResponse((yield fetch(`${apiUrl}/retrieve/filter?name=${request.filter}&value=${request.value}`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getStoresByFilter(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

const getStoreSitemap = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl) {
        return validateResponse(storeSitemapResponseSpec, (yield fetch(`${apiUrl}/sitemap`, {
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        })));
    });
    return function getStoreSitemap(apiUrl) {
        return _ref.apply(this, arguments);
    };
}();

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getTowns: getTowns,
  list: list,
  getDetailsByCode: getDetailsByCode,
  getStoresByFilter: getStoresByFilter,
  getStoreSitemap: getStoreSitemap
});

var types$1 = /*#__PURE__*/Object.freeze({
  __proto__: null
});

var types = /*#__PURE__*/Object.freeze({
  __proto__: null
});

const getConfigResponse = Type.interface({
    scope: Type.string,
    maxFileSizeBytes: Type.number,
    maxFileNameLength: Type.number,
    allowedFileTypes: optional(Type.array(Type.string)),
    isRecaptchaTokenRequired: Type.boolean
});
const getUploadTokenResponse = Type.interface({
    scope: Type.string,
    token: Type.string
});
const sendFileResponse = Type.interface({
    scope: Type.string,
    storageId: Type.string,
    storageToken: Type.string
});

const getConfig = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { scope  }, init) {
        return validateResponse(getConfigResponse, (yield fetch(`${apiUrl}/upload/config/${scope}`, swcHelpers.objectSpread({
            method: 'GET',
            headers: swcHelpers.objectSpread({}, acceptJson)
        }, init))));
    });
    return function getConfig(apiUrl, _, init) {
        return _ref.apply(this, arguments);
    };
}();

const getUploadToken = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { scope , referenceId , recaptchaToken  }, init) {
        const formData = new FormData();
        formData.append('scope', scope);
        formData.append('referenceId', referenceId);
        if (recaptchaToken) {
            formData.append('recaptchaToken', recaptchaToken);
        }
        return validateResponse(getUploadTokenResponse, (yield fetch(`${apiUrl}/upload/token`, swcHelpers.objectSpread({
            method: 'POST',
            body: formData
        }, init))));
    });
    return function getUploadToken(apiUrl, _, init) {
        return _ref.apply(this, arguments);
    };
}();

const sendFile = function() {
    var _ref = swcHelpers.asyncToGenerator(function*(apiUrl, { scope , referenceId , uploadedFile , uploadToken , alias  }, init) {
        const formData = new FormData();
        formData.append('scope', scope);
        formData.append('referenceId', referenceId);
        formData.append('uploadedFile', uploadedFile, alias || uploadedFile.name);
        return validateResponse(sendFileResponse, (yield fetch(`${apiUrl}/upload/file`, swcHelpers.objectSpread({
            method: 'POST',
            body: formData,
            headers: {
                'x-upload-token': uploadToken
            }
        }, init))));
    });
    return function sendFile(apiUrl, _, init) {
        return _ref.apply(this, arguments);
    };
}();

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Types: types,
  getConfig: getConfig,
  getUploadToken: getUploadToken,
  sendFile: sendFile
});

const initialState = [
    true,
    undefined,
    undefined,
    false
];
function reducer(state, action) {
    switch(action.type){
        case 'RESET':
            return initialState;
        case 'CALLED':
            return [
                true,
                undefined,
                undefined,
                true
            ];
        case 'SUCCESS':
            return [
                false,
                action.payload,
                undefined,
                true
            ];
        case 'FAILURE':
            return [
                false,
                undefined,
                action.payload,
                true
            ];
    }
}
/**
 * Use one of the api-client's functions as a hook. The first argument is the
 * API function itself. The rest of the argument are the API function's
 * parameters.
 *
 * For example:
 *
 *    const [loading, result, error] = useApiClient(
 *      Orders.list,
 *      apiUrl,
 *      accessToken,
 *      { customerId, limit, offset }
 *    )
 *
 *  Note: the type of the rest arguments and of `result` are inferred from the
 *  given API function.
 */ function useApiClient(apiFunc, options, ...params) {
    const [state, dispatch] = useReducer(reducer, initialState);
    useEffect(()=>{
        let cancelled = false;
        dispatch({
            type: 'RESET'
        });
        if (options && options.skip) {
            return;
        }
        dispatch({
            type: 'CALLED'
        });
        apiFunc(...params) // TODO: Currently, `any` is required because of skip functionality
        .then((result)=>cancelled || dispatch({
                type: 'SUCCESS',
                payload: result
            })
        ).catch((error)=>cancelled || dispatch({
                type: 'FAILURE',
                payload: error
            })
        );
        return ()=>{
            cancelled = true;
        };
    }, [
        options && options.skip,
        ...params
    ]);
    return state;
}

export { index$f as Appointments, index$e as Carts, index$9 as Customers, index$8 as Location, index$7 as Optimizely, index$6 as Orders, types$2 as PaymentTypes, index$4 as Payments, index$3 as Products, ResponseError, index$2 as Status, index$1 as Stores, types$1 as Types, index as Upload, useApiClient };
//# sourceMappingURL=index.js.map
